import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const FAQPage = () => {
  usePageMetadata();
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "For Renters",
      questions: [
        {
          q: "What is mid-term housing?",
          a: "Mid-term housing refers to furnished rentals for stays typically between 30 days and 12 months. It's perfect for relocations, extended work assignments, travel nurses, or anyone needing flexible housing."
        },
        {
          q: "How do I book a property?",
          a: "Browse listings, find a property you like, and click 'Request to Book.' You can message the owner with questions first. Once approved, complete payment through our secure platform."
        },
        {
          q: "Is there a security deposit?",
          a: "Most properties require a security deposit, typically one month's rent. The exact amount is shown on each listing. Deposits are held securely and returned after move-out inspection."
        },
        {
          q: "Can I tour the property before booking?",
          a: "Yes! We encourage renters to request a tour before booking. Message the owner through our platform to schedule a visit, or ask for a video tour if you're remote."
        },
        {
          q: "What's included in the rent?",
          a: "Mid-term rentals typically include furniture, basic utilities, WiFi, and kitchen essentials. Each listing specifies exactly what's included. Always check the amenities list."
        }
      ]
    },
    {
      category: "For Property Owners",
      questions: [
        {
          q: "How much does it cost to list my property?",
          a: "We offer three subscription plans: Standard ($99/mo), Featured ($149/mo), and Concierge ($199/mo). There are no per-booking fees or commissions on rent."
        },
        {
          q: "How do I receive payments?",
          a: "Payments are processed securely through Stripe. Once you set up your payout account, rent payments are deposited directly to your bank account."
        },
        {
          q: "Can I screen renters?",
          a: "Stay Pair Homes takes a human-first approach to renter screening. We do not use traditional screening tools like credit reports or background checks.\n\nInstead, we empower property owners to make their own informed decisions through direct communication. Owners can review renter profiles, message potential renters, ask questions, and use their judgment to decide who feels like the right fit for a short-term stay.\n\nThis approach reduces costs and administrative hurdles for owners while opening the door to renters who may be in transition — such as those rebuilding credit, relocating for work, or needing a temporary place to stay. Many of these renters are responsible individuals who simply need an opportunity.\n\nWe believe meaningful conversations and owner discretion are often more effective than automated screening systems."
        },
        {
          q: "What if a renter damages my property?",
          a: "Security deposits help cover minor damages. We recommend documenting property condition with photos before and after each stay. For significant issues, our support team can help mediate."
        },
        {
          q: "Can I set my own house rules?",
          a: "Yes. You can specify house rules in your listing including pet policies, smoking rules, guest limits, quiet hours, and any other requirements."
        }
      ]
    },
    {
      category: "Payments & Billing",
      questions: [
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit and debit cards through our secure payment processor, Stripe. Bank transfers are available in some cases."
        },
        {
          q: "When is rent due?",
          a: "Payment terms are agreed upon between the owner and renter. Typically, the first month's rent and deposit are due at booking, with subsequent payments monthly."
        },
        {
          q: "What is your cancellation policy?",
          a: "Cancellation policies vary by listing. Owners can choose Flexible, Moderate, or Strict policies. The specific policy is shown on each property listing."
        },
        {
          q: "How do refunds work?",
          a: "Refunds follow the property's cancellation policy. If eligible, refunds are processed within 5-10 business days back to your original payment method."
        }
      ]
    },
    {
      category: "Account & Support",
      questions: [
        {
          q: "How do I create an account?",
          a: "Click 'Sign Up' and choose whether you're a renter or property owner. You can sign up with email or use Google for quick registration."
        },
        {
          q: "I forgot my password. What do I do?",
          a: "Click 'Forgot Password' on the login page and enter your email. We'll send you a link to reset your password."
        },
        {
          q: "How do I contact support?",
          a: "Visit our Contact page or email support@staypairhomes.com. Our team typically responds within 24 hours."
        },
        {
          q: "Is my personal information secure?",
          a: "Yes. We use industry-standard encryption and security practices to protect your data. See our Privacy Policy for details."
        }
      ]
    }
  ];

  const filteredFaqs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(
      faq => 
        faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <Layout>
      <div className="bg-gradient-to-b from-slate-50 to-white min-h-screen py-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Find answers to common questions about Stay Pair Homes
            </p>
            
            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-8">
            {filteredFaqs.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">{category.category}</h2>
                <div className="space-y-3">
                  {category.questions.map((faq, questionIndex) => {
                    const isOpen = openIndex === `${categoryIndex}-${questionIndex}`;
                    return (
                      <div 
                        key={questionIndex}
                        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                      >
                        <button
                          onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                        >
                          <span className="font-medium text-slate-900 pr-4">{faq.q}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <p className="text-slate-600">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {filteredFaqs.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500">No questions found matching your search.</p>
              </div>
            )}
          </div>

          {/* Contact CTA */}
          <div className="mt-12 bg-trust-blue/5 rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Still have questions?</h3>
            <p className="text-slate-600 mb-4">We're here to help!</p>
            <a 
              href="/contact" 
              className="inline-flex items-center justify-center px-6 py-3 bg-trust-blue text-white rounded-xl font-medium hover:bg-trust-blue-hover transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FAQPage;
